/**
 * Vue directive: `v-prevent-browser-gestures`
 *
 * Suppresses browser navigation swipes and pull-to-refresh on the element.
 *
 * Register globally in main.ts:
 *   import { vPreventBrowserGestures } from "@/libraries/prevent-browser-gestures";
 *   app.directive("prevent-browser-gestures", vPreventBrowserGestures);
 *
 * Usage:
 *   <div v-prevent-browser-gestures />              — suppress both axes via CSS only
 *   <div v-prevent-browser-gestures="'x'" />        — horizontal only
 *   <div v-prevent-browser-gestures="'y'" />        — vertical only
 *   <div v-prevent-browser-gestures="'both'" />     — same as default
 *   <div v-prevent-browser-gestures="'all'" />      — alias for 'both'
 *   <div v-prevent-browser-gestures="'pull-to-refresh'" />
 *   <div v-prevent-browser-gestures="'navigation-swipe'" />
 *   <div v-prevent-browser-gestures="'horizontal'" />
 *   <div v-prevent-browser-gestures="'vertical'" />
 *   <div v-prevent-browser-gestures.events-too="'both'" />
 */

import type { Directive, DirectiveBinding } from 'vue';
import { applyPreventGestures, type GestureMode, type PreventGesturesInstance } from './core';

const KEY = Symbol('preventBrowserGestures');

type ElementWithInstance = HTMLElement & {
  [KEY]?: PreventGesturesInstance;
};

type GestureBinding = GestureMode | boolean | '' | null | undefined;

function resolveGestureMode(value: GestureBinding): GestureMode | null {
  if (value === false || value === null) {
    return null;
  }

  return value && typeof value === 'string'
    ? value
    : 'both';
}

export const vPreventBrowserGestures: Directive<HTMLElement, GestureBinding> = {
  mounted(el: ElementWithInstance, binding: DirectiveBinding<GestureBinding>) {
    const mode = resolveGestureMode(binding.value);
    if (mode === null) return;

    el[KEY] = applyPreventGestures(el, mode, {
      eventsToo: binding.modifiers['events-too'] === true,
    });
  },

  updated(el: ElementWithInstance, binding: DirectiveBinding<GestureBinding>) {
    el[KEY]?.destroy();
    delete el[KEY];

    const mode = resolveGestureMode(binding.value);
    if (mode === null) return;

    el[KEY] = applyPreventGestures(el, mode, {
      eventsToo: binding.modifiers['events-too'] === true,
    });
  },

  unmounted(el: ElementWithInstance) {
    el[KEY]?.destroy();
    delete el[KEY];
  },
};
